import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as s from './styled-customers';
import {
  EmptyState,
  Icon,
  ModalClose,
  ModalConfirmation,
} from '../../components';
import { icons } from '../../assets';
import {
  getCustomers,
  postEditClient,
  postEditStatus,
  postNewClient,
  setCustomerTariff,
} from '../../services';
import {
  invalidateSessionCache,
  justNumbers,
  notifyError,
  notifySuccess,
  sessionCache,
  useDebounce,
} from '../../utils';

// Lazy-load do modal de cadastro/edicao — bundle pesado (Formik + yup
// + InputMask). So carrega quando o operador abre o modal.
const CadastroClienteModal = lazy(() =>
  import('../../components/customer-form/customer-form'),
);

// Inline icons (lucide-style) — usados no chrome da tela. Acao de
// editar continua usando <Icon name="pencil"> porque ja resolve.
const ISearch = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const IPlus = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

// Converte o input da tarifa -> numero ou null (vazio remove). undefined =
// invalido (nao salva).
const parseTariff = (str) => {
  const t = (str || '').trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
};

const ICheck = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);
const IX = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const RelacaoClientes = () => {
  // Fetch principal (lista paginada/filtrada).
  const [currentData, setCurrentData] = useState([]);
  const [amount, setAmount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filtros do design Clientes.html: busca + status + empresa.
  const [filterText, setFilterText] = useState('');
  const debouncedFilterText = useDebounce(filterText, 350);
  const [currentStatus, setCurrentStatus] = useState('');
  const [currentEmpresa, setCurrentEmpresa] = useState('');

  // Paginacao.
  const [currentPage, setCurrentPage] = useState(0);
  const [itensPerPage, setItensPerPage] = useState(10);

  // Lista de TODAS as empresas pra popular o select "Empresa" (filtro).
  // Cache de sessao via sessionCache pra nao re-fetchar a cada mount.
  const [allCustomers, setAllCustomers] = useState([]);

  // Toggle "Ativacao" sincroniza com backend. Mantem o id em-voo pra
  // desabilitar o toggle dessa linha enquanto a request roda (evita
  // double-click + race).
  const [togglingId, setTogglingId] = useState(null);

  // Edicao inline da TARIFA padrao do cliente (R$/kWh). editingTariffId =
  // linha em edicao; tariffInput = valor do campo; savingTariffId = em-voo.
  const [editingTariffId, setEditingTariffId] = useState(null);
  const [tariffInput, setTariffInput] = useState('');
  const [savingTariffId, setSavingTariffId] = useState(null);

  // Modal de cadastro/edicao. cliente == null => criar novo;
  // cliente preenchido => editar.
  const [isCadastroOpen, setIsCadastroOpen] = useState(false);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [telaConcluido, setTelaConcluido] = useState(false);
  const [closeConfirmation, setCloseConfirmation] = useState(false);
  // Modal de confirmacao de edicao (passo 2 do fluxo do CadastroClienteModal).
  const [confirmationModal, setConfirmationModal] = useState(false);
  // Valores do form retornados pelo CadastroClienteModal quando o user
  // confirma o save (a modal chama submit(values) -> aqui).
  const [pendingEditValues, setPendingEditValues] = useState(null);

  const formikInitialValues = useMemo(
    () => ({
      sigla: '',
      name: '',
      cnpj: '',
      email: '',
      endereco: '',
      estado: '',
    }),
    [],
  );

  // Token de fetch — incrementado a cada nova busca. Quando uma response
  // chega, comparamos com o token atual: se for diferente, descarta. Evita
  // race quando o operador troca filtros rapido (request antiga chega
  // depois da nova e sobrescreve dados corretos).
  const fetchTokenRef = useRef(0);

  const buscaClientes = useCallback(() => {
    fetchTokenRef.current += 1;
    const myToken = fetchTokenRef.current;
    setLoading(true);
    getCustomers(
      currentPage + 1,
      itensPerPage,
      debouncedFilterText,
      currentEmpresa,
      currentStatus,
    )
      .then(({ data }) => {
        if (myToken !== fetchTokenRef.current) return;
        setCurrentData(data[0] || []);
        setAmount(data[1] || 0);
      })
      .catch(() => {
        if (myToken !== fetchTokenRef.current) return;
        notifyError('Nao foi possivel buscar os clientes');
        setCurrentData([]);
        setAmount(0);
      })
      .finally(() => {
        if (myToken === fetchTokenRef.current) setLoading(false);
      });
  }, [
    currentPage,
    itensPerPage,
    debouncedFilterText,
    currentEmpresa,
    currentStatus,
  ]);

  // Lista completa de clientes pro select de filtro. Cache de sessao
  // — so re-fetcha se o operador F5 a aba ou abrir nova sessao.
  const loadAllCustomers = useCallback(async () => {
    try {
      const resp = await sessionCache('allCustomers', 'all', () =>
        getCustomers('', '', '', '', ''),
      );
      setAllCustomers((resp && resp.data && resp.data[0]) || []);
    } catch (err) {
      // Falha silenciosa — o select de empresa fica vazio mas a tela
      // continua funcional (busca por texto ainda funciona).
    }
  }, []);

  useEffect(() => {
    buscaClientes();
    // Cleanup: bump token pra ignorar response em-voo quando o componente
    // desmontar (evita setState em componente desmontado).
    return () => {
      fetchTokenRef.current += 1;
    };
  }, [buscaClientes]);

  useEffect(() => {
    loadAllCustomers();
  }, [loadAllCustomers]);

  // Reseta pra pagina 1 quando filtros/itensPerPage mudam.
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedFilterText, itensPerPage, currentEmpresa, currentStatus]);

  // Toggle de ativacao otimista: atualiza UI imediato, reverte se a
  // request falhar. Evita feeling de "lag" no clique.
  const toggleStatus = async (customer) => {
    if (togglingId) return;
    const newStatus = !customer.status;
    setTogglingId(customer.id);
    // Update otimista
    setCurrentData((rows) =>
      rows.map((r) => (r.id === customer.id ? { ...r, status: newStatus } : r)),
    );
    try {
      await postEditStatus(customer.id, newStatus);
      notifySuccess(
        newStatus
          ? `Cliente "${customer.name}" ativado.`
          : `Cliente "${customer.name}" desativado.`,
      );
    } catch (err) {
      // Reverte
      setCurrentData((rows) =>
        rows.map((r) =>
          r.id === customer.id ? { ...r, status: !newStatus } : r,
        ),
      );
      notifyError(`Erro ao alterar status de "${customer.name}".`);
    } finally {
      setTogglingId(null);
    }
  };

  // --- Tarifa inline ---
  const startEditTariff = (row) => {
    setEditingTariffId(row.id);
    setTariffInput(row.tariff != null ? String(row.tariff) : '');
  };
  const cancelEditTariff = () => {
    setEditingTariffId(null);
    setTariffInput('');
  };
  const saveTariff = async (row) => {
    const val = parseTariff(tariffInput);
    if (val === undefined) {
      notifyError('Tarifa inválida.');
      return;
    }
    setSavingTariffId(row.id);
    try {
      await setCustomerTariff(row.id, val);
      // Reflete localmente sem refetch.
      setCurrentData((rows) =>
        rows.map((r) => (r.id === row.id ? { ...r, tariff: val } : r)),
      );
      notifySuccess(`Tarifa de "${row.name}" atualizada.`);
      cancelEditTariff();
    } catch (err) {
      notifyError('Não foi possível salvar a tarifa.');
    } finally {
      setSavingTariffId(null);
    }
  };

  // Submit cadastro novo (modal aberta sem clienteSelecionado).
  const cadastrarClienteNovo = (values) => {
    postNewClient({
      sigla: values.sigla,
      name: values.name,
      cnpj: justNumbers(values.cnpj),
      email: values.email,
      endereco: values.endereco,
      estado: values.estado,
      companyId: 1,
    })
      .then(() => {
        setTelaConcluido(true);
      })
      .catch((error) => {
        if (error && error.message) {
          if (Array.isArray(error.message)) {
            error.message.forEach((m) => notifyError(m));
          } else {
            notifyError(error.message);
          }
        } else {
          notifyError('Ocorreu um erro ao salvar o cliente');
        }
      });
  };

  // Submit edicao — fluxo do CadastroClienteModal e: submit(values)
  // captura values + abre modal de confirmacao; ao confirmar chama
  // editarCliente abaixo.
  const editarCliente = (values) => {
    if (!clienteSelecionado || !values) return;
    postEditClient(clienteSelecionado.id, {
      sigla: values.sigla,
      name: values.name,
      email: values.email,
      endereco: values.endereco,
      estado: values.estado,
    })
      .then(() => {
        setConfirmationModal(false);
        setTelaConcluido(true);
      })
      .catch((error) => {
        if (error && error.message) notifyError(error.message);
        else notifyError('Ocorreu um erro ao salvar o cliente');
        setConfirmationModal(false);
      });
  };

  const handleOpenCadastro = () => {
    setClienteSelecionado(null);
    setIsCadastroOpen(true);
  };

  const handleOpenEdit = (row) => {
    setClienteSelecionado(row);
    setIsCadastroOpen(true);
  };

  const handleCloseModal = () => {
    setIsCadastroOpen(false);
    setClienteSelecionado(null);
    setTelaConcluido(false);
    setPendingEditValues(null);
    buscaClientes();
    // Refresca lista de empresas (caso o user tenha cadastrado novo).
    invalidateSessionCache('allCustomers', 'all');
    loadAllCustomers();
  };

  const pageCount = Math.ceil(amount / itensPerPage) || 0;

  // Lista de paginas com elipses (1, 2, 3, ..., n-2, n-1, n).
  const pagList = useMemo(() => {
    const list = [];
    const total = pageCount;
    const pg = currentPage + 1;
    if (total <= 7) {
      for (let i = 1; i <= total; i += 1) list.push(i);
    } else {
      list.push(1, 2, 3);
      if (pg > 5) list.push('...');
      if (pg > 3 && pg < total - 2) list.push(pg);
      if (pg < total - 4) list.push('...');
      list.push(total - 2, total - 1, total);
    }
    return Array.from(new Set(list));
  }, [pageCount, currentPage]);

  return (
    <s.Container className="fade-in">
      {/* Page Header — titulo + sub + contador */}
      <s.PageHeader>
        <s.PageHeaderText>
          <s.PageTitle>Clientes</s.PageTitle>
          <s.PageSub>Gerencie empresas, contatos e ativacao.</s.PageSub>
        </s.PageHeaderText>
        <s.PageCount>
          <b>{amount}</b> {amount === 1 ? 'cliente' : 'clientes'}
        </s.PageCount>
      </s.PageHeader>

      {/* Filters — grid 4-col: busca | Status | Empresa | CTA */}
      <s.Filters>
        <s.Search>
          {ISearch}
          <input
            value={filterText}
            onChange={(event) => setFilterText(event.target.value)}
            placeholder="Pesquise por empresa, e-mail ou razao social"
          />
        </s.Search>

        <s.SelCell>
          <span className="k">Status</span>
          <select
            value={currentStatus}
            onChange={(e) => setCurrentStatus(e.target.value)}
          >
            <option value="">Selecione</option>
            <option value="true">Ativo</option>
            <option value="false">Inativo</option>
          </select>
        </s.SelCell>

        <s.SelCell>
          <span className="k">Empresa</span>
          <select
            value={currentEmpresa}
            onChange={(e) => setCurrentEmpresa(e.target.value)}
          >
            <option value="">Selecione</option>
            {allCustomers
              .slice()
              .sort((a, b) =>
                (a.name || '').localeCompare(b.name || '', 'pt-BR', {
                  sensitivity: 'base',
                }),
              )
              .map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </s.SelCell>

        <s.Cta type="button" onClick={handleOpenCadastro}>
          <span className="ic">{IPlus}</span>
          <span className="tx">
            <span className="t">Cadastro</span>
            <span className="s">Inserir cliente</span>
          </span>
        </s.Cta>
      </s.Filters>

      {/* Table — 5-col grid: Empresa | Email | Status | Ativacao | Editar */}
      <s.TableWrap>
        <s.Table>
          <s.Th $center>Editar</s.Th>
          <s.Th $center>Empresa</s.Th>
          <s.Th $center>Email de Contato</s.Th>
          <s.Th $center>Tarifa (R$/kWh)</s.Th>
          <s.Th $center>Status</s.Th>
          <s.Th $center>Ativacao</s.Th>

          {loading ? (
            Array.from(
              { length: itensPerPage },
              (_, i) => `sk-${itensPerPage}-${i}`,
            ).map((key) => (
              <s.Tr key={key}>
                <s.SkeletonCell $w="36px" $center />
                <s.SkeletonCell $w="80%" />
                <s.SkeletonCell $w="70%" />
                <s.SkeletonCell $w="50%" $center />
                <s.SkeletonCell $w="40%" $center />
                <s.SkeletonCell $w="40%" $center />
              </s.Tr>
            ))
          ) : currentData.length ? (
            currentData.map((row) => (
              <s.Tr key={row.id}>
                <s.Tc $center data-label="Editar">
                  <s.RowActions>
                    <s.IconBtn
                      type="button"
                      title="Editar cliente"
                      aria-label={`Editar cliente ${row.name}`}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Icon name="pencil" size={16} />
                    </s.IconBtn>
                  </s.RowActions>
                </s.Tc>
                <s.TcName $center>{row.name}</s.TcName>
                <s.TcEmail $center>{row.email}</s.TcEmail>
                <s.TcTariff $center>
                  {editingTariffId === row.id ? (
                    <s.TariffEditor>
                      <s.TariffInput
                        type="text"
                        inputMode="decimal"
                        autoFocus
                        value={tariffInput}
                        placeholder="0,00"
                        onChange={(e) => setTariffInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveTariff(row);
                          if (e.key === 'Escape') cancelEditTariff();
                        }}
                      />
                      <s.TariffIconBtn
                        type="button"
                        $primary
                        title="Salvar tarifa"
                        aria-label="Salvar tarifa"
                        disabled={savingTariffId === row.id}
                        onClick={() => saveTariff(row)}
                      >
                        {ICheck}
                      </s.TariffIconBtn>
                      <s.TariffIconBtn
                        type="button"
                        title="Cancelar"
                        aria-label="Cancelar edição da tarifa"
                        disabled={savingTariffId === row.id}
                        onClick={cancelEditTariff}
                      >
                        {IX}
                      </s.TariffIconBtn>
                    </s.TariffEditor>
                  ) : (
                    <s.TariffPill
                      type="button"
                      $empty={row.tariff == null}
                      title="Definir tarifa padrão do cliente"
                      onClick={() => startEditTariff(row)}
                    >
                      {row.tariff != null
                        ? `R$ ${Number(row.tariff).toLocaleString('pt-BR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`
                        : 'definir'}
                    </s.TariffPill>
                  )}
                </s.TcTariff>
                <s.Tc $center data-label="Status">
                  <s.StatusPill $on={!!row.status}>
                    {row.status ? 'Ativo' : 'Inativo'}
                  </s.StatusPill>
                </s.Tc>
                <s.Tc $center data-label="Ativacao">
                  <s.Toggle
                    type="button"
                    $on={!!row.status}
                    disabled={togglingId === row.id}
                    title={row.status ? 'Desativar cliente' : 'Ativar cliente'}
                    aria-label={
                      row.status ? 'Desativar cliente' : 'Ativar cliente'
                    }
                    onClick={() => toggleStatus(row)}
                  />
                </s.Tc>
              </s.Tr>
            ))
          ) : (
            <s.EmptyRow>
              <EmptyState
                title="Nenhum cliente encontrado"
                description="Ajuste a busca ou os filtros e tente novamente."
                actionLabel="Limpar filtros"
                onAction={() => {
                  setFilterText('');
                  setCurrentStatus('');
                  setCurrentEmpresa('');
                }}
              />
            </s.EmptyRow>
          )}
        </s.Table>
      </s.TableWrap>

      {/* Pager — Anterior / 1 2 3 ... 45 46 47 / Proximo + exibir N */}
      <s.Pager>
        <button
          className="btn"
          type="button"
          onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
          disabled={currentPage === 0 || pageCount === 0}
        >
          Anterior
        </button>
        <div className="pages">
          {pagList.map((p, idx) =>
            p === '...' ? (
              // eslint-disable-next-line react/no-array-index-key
              <span key={`dots-${idx}`} className="dots">
                ...
              </span>
            ) : (
              <button
                key={p}
                type="button"
                className={`pg ${currentPage + 1 === p ? 'on' : ''}`}
                onClick={() => setCurrentPage(p - 1)}
              >
                {p}
              </button>
            ),
          )}
        </div>
        <button
          className="btn"
          type="button"
          onClick={() =>
            setCurrentPage((p) => Math.min(pageCount - 1, p + 1))
          }
          disabled={currentPage >= pageCount - 1 || pageCount === 0}
        >
          Proximo
        </button>
        <div className="exhibit">
          exibir
          <select
            value={itensPerPage}
            onChange={(e) => setItensPerPage(Number(e.target.value))}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
      </s.Pager>

      {/* Modal cadastro/edicao — preserva fluxo legado do CadastroClienteModal.
          submit() recebe o values do formik; em modo edit, abre confirmation
          modal antes do PUT. Em modo create, dispara o POST direto. */}
      {isCadastroOpen ? (
        <Suspense fallback={null}>
        <CadastroClienteModal
          onClose={handleCloseModal}
          onCloseConfirmation={setCloseConfirmation}
          submit={
            clienteSelecionado
              ? (values) => {
                  // Em edicao: capta values + abre confirmation modal.
                  setPendingEditValues(values);
                  setConfirmationModal(true);
                }
              : cadastrarClienteNovo
          }
          setConfirmationModal={setConfirmationModal}
          telaConcluido={telaConcluido}
          setTelaConcluido={setTelaConcluido}
          formikInitialValues={clienteSelecionado || formikInitialValues}
          title={clienteSelecionado ? 'Editar Cliente' : 'Cadastro de Cliente'}
          dadosAbaixo={
            clienteSelecionado
              ? 'Modifique os dados abaixo:'
              : 'Preencha os dados abaixo:'
          }
          msgButton={
            clienteSelecionado
              ? 'Confirme suas modificacoes'
              : 'Sera enviado um convite para o email cadastrado.'
          }
          labelButton={clienteSelecionado ? 'SALVAR' : 'CADASTRAR'}
          sucesso={
            clienteSelecionado
              ? 'Cliente editado com sucesso!'
              : 'Cliente cadastrado com sucesso!'
          }
          estadoValue={clienteSelecionado ? clienteSelecionado.estado : ''}
          iconFinal={
            clienteSelecionado
              ? icons.ClientesIconEdit
              : icons.ClientesIconFinish
          }
        />
        </Suspense>
      ) : null}

      {/* Confirmacao de edicao (passo 2 do fluxo). */}
      {confirmationModal ? (
        <ModalConfirmation
          onClose={() => editarCliente(pendingEditValues)}
          onCloseConfirmation={setConfirmationModal}
        />
      ) : null}

      {/* Confirmacao de fechar modal sem salvar. */}
      {closeConfirmation ? (
        <ModalClose
          onClose={() => {
            setIsCadastroOpen(false);
            setTelaConcluido(false);
            setCloseConfirmation(false);
            setPendingEditValues(null);
            setClienteSelecionado(null);
          }}
          onCloseConfirmation={setCloseConfirmation}
        />
      ) : null}
    </s.Container>
  );
};

export default RelacaoClientes;
